// A caixa de digitar.
//
// Não ramifica por canal: quem liga cada recurso é a capability da conexão.
// `supportsSubject` liga o campo de assunto (hoje só e-mail), `bodyFormat`
// diria se o corpo é HTML, e `supportsTemplates` é o que permitiria falar fora
// da janela de resposta em canal oficial.
//
// JANELA DE 24 HORAS. Em canal com `hasResponseWindow` — hoje só o WhatsApp
// oficial —, passadas 24h desde a última mensagem DO CLIENTE a Meta recusa
// texto livre; só template aprovado sai. O aviso aparece ANTES de digitar, e a
// caixa fica travada.
//
// O limite de 4096 caracteres não é enfeite: `assertCanSend` recusa acima
// disso, e a recusa chega depois do envio — melhor avisar antes de digitar.
//
// FASE 14 — RESPOSTAS RÁPIDAS. Digitar "/" no começo da linha abre a lista, e a
// lista já vem FILTRADA PELO CANAL da conversa.
//
// WHATSAPP COMPLETO (23/10/2026). Quando quem usa passa `onEnviarArquivos`,
// a caixa ganha: anexar (vários de uma vez, com prévia), colar print, arrastar
// arquivo, gravar mensagem de voz, figurinhas e "respondendo a…". O emoji vale
// para qualquer canal. O envio em si é de quem usa (`envioRico.ts`): esta caixa
// só junta o que a pessoa escolheu.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle, Clock, File as IconeArquivo, Image as IconeImagem, Loader2, Megaphone, Mic, Paperclip,
  Send, Smile, Sticker, Trash2, Video, X, Zap,
} from 'lucide-react';
import { estadoDaJanela } from '../janelaResposta';
import type { Capabilities, ConexaoPublica } from '../tipos';
import type { RespostaRapida } from '../apiOrganizacao';
import { filtrarPorCanal } from '../apiOrganizacao';
import type { Figurinha, ItemParaEnviar } from '../envioRico';
import { tamanhoLegivel } from '../formato';
import { GRUPOS_EMOJI } from './emojis';

const LIMITE = 4096;
const MB = 1024 * 1024;
/** Mesmos tetos do servidor (`acoesConversa.ts`): avisa antes de subir à toa. */
const TETO_MIDIA = 16 * MB;
const TETO_DOCUMENTO = 50 * MB;
/** Mensagem de voz para sozinha aqui, como no app. */
const VOZ_MAXIMA_S = 5 * 60;

interface Props {
  conexao: ConexaoPublica | null;
  enviando: boolean;
  erro: string | null;
  respostasRapidas?: RespostaRapida[];
  /** Fase 13 — a conversa é uma thread de comentários de post. */
  publica?: boolean;
  /** Fim da janela de resposta. Null quando o canal não tem janela. */
  janelaExpiraEm?: string | null;
  onEnviar: (texto: string) => void;
  onLimparErro: () => void;
  /** WhatsApp completo: sem ele a caixa fica só com texto. */
  onEnviarArquivos?: (itens: ItemParaEnviar[], legenda: string) => void;
  onEnviarFigurinha?: (f: Figurinha) => void;
  listarFigurinhas?: () => Promise<Figurinha[]>;
  /** A mensagem que está sendo respondida (citada). */
  respondendo?: { autor: string; texto: string } | null;
  onCancelarResposta?: () => void;
}

interface Pendente extends ItemParaEnviar {
  id: string;
  previa: string | null;
}

function ehMidia(mime: string) {
  return mime.startsWith('image/') || mime.startsWith('video/') || mime.startsWith('audio/');
}

function extensaoDoAudio(mime: string) {
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('mp4')) return 'm4a';
  return 'webm';
}

export default function Composer({
  conexao, enviando, erro, respostasRapidas = [], publica = false, janelaExpiraEm = null,
  onEnviar, onLimparErro, onEnviarArquivos, onEnviarFigurinha, listarFigurinhas, respondendo, onCancelarResposta,
}: Props) {
  const [texto, setTexto] = useState('');
  const [listaAberta, setListaAberta] = useState(false);
  const [painel, setPainel] = useState<'emoji' | 'figurinhas' | null>(null);
  const [pendentes, setPendentes] = useState<Pendente[]>([]);
  const [aviso, setAviso] = useState<string | null>(null);
  const [arrastando, setArrastando] = useState(false);
  const [figurinhas, setFigurinhas] = useState<Figurinha[] | null>(null);
  const [carregandoFigurinhas, setCarregandoFigurinhas] = useState(false);
  const [gravandoDesde, setGravandoDesde] = useState<number | null>(null);
  const [agora, setAgora] = useState(Date.now());

  const ref = useRef<HTMLTextAreaElement>(null);
  const inputArquivo = useRef<HTMLInputElement>(null);
  const inputFigurinha = useRef<HTMLInputElement>(null);
  const gravador = useRef<MediaRecorder | null>(null);
  const cancelarGravacao = useRef(false);

  const caps = (conexao?.capabilities ?? {}) as Partial<Capabilities>;
  const canalCaido = conexao?.status === 'error' || conexao?.status === 'disconnected';
  const rico = !!onEnviarArquivos;
  const podeGravar = rico && caps.supportsAudioMessage !== false;

  const janela = useMemo(
    () => estadoDaJanela(caps.hasResponseWindow, janelaExpiraEm),
    [caps.hasResponseWindow, janelaExpiraEm],
  );

  const disponiveis = useMemo(
    () => filtrarPorCanal(respostasRapidas, conexao?.channel_type ?? null),
    [respostasRapidas, conexao?.channel_type],
  );

  const filtro = listaAberta && texto.startsWith('/') ? texto.slice(1).toLowerCase() : '';
  const sugestoes = listaAberta
    ? disponiveis.filter((r) => r.atalho.toLowerCase().includes(filtro)).slice(0, 6)
    : [];

  // Prévias (blob) são liberadas quando o item sai da lista.
  useEffect(() => () => pendentes.forEach((p) => p.previa && URL.revokeObjectURL(p.previa)), []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [texto]);

  // Relógio da gravação.
  useEffect(() => {
    if (!gravandoDesde) return;
    const id = window.setInterval(() => {
      setAgora(Date.now());
      if ((Date.now() - gravandoDesde) / 1000 >= VOZ_MAXIMA_S) pararGravacao(true);
    }, 250);
    return () => window.clearInterval(id);
  }, [gravandoDesde]); // eslint-disable-line react-hooks/exhaustive-deps

  const usarResposta = (r: RespostaRapida) => {
    setTexto(r.body);
    setListaAberta(false);
    ref.current?.focus();
  };

  // ── Arquivos ──
  const adicionarArquivos = (lista: FileList | File[]) => {
    if (!rico) return;
    const novos: Pendente[] = [];
    for (const f of Array.from(lista)) {
      const mime = f.type || 'application/octet-stream';
      const teto = ehMidia(mime) ? TETO_MIDIA : TETO_DOCUMENTO;
      if (f.size > teto) {
        setAviso(`"${f.name}" passa do limite de ${teto / MB} MB.`);
        continue;
      }
      if (!f.size) continue;
      novos.push({
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        arquivo: f,
        nome: f.name || 'arquivo',
        previa: mime.startsWith('image/') || mime.startsWith('video/') ? URL.createObjectURL(f) : null,
      });
    }
    if (novos.length) {
      setPendentes((atuais) => [...atuais, ...novos].slice(0, 10));
      setPainel(null);
      ref.current?.focus();
    }
  };

  const tirarPendente = (id: string) => {
    setPendentes((atuais) => {
      const sai = atuais.find((p) => p.id === id);
      if (sai?.previa) URL.revokeObjectURL(sai.previa);
      return atuais.filter((p) => p.id !== id);
    });
  };

  const aoColar = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!rico) return;
    const arquivos = Array.from(e.clipboardData?.files ?? []);
    if (arquivos.length) {
      e.preventDefault();
      adicionarArquivos(arquivos);
    }
  };

  // ── Figurinhas ──
  const abrirFigurinhas = async () => {
    setPainel((p) => (p === 'figurinhas' ? null : 'figurinhas'));
    if (figurinhas || !listarFigurinhas) return;
    setCarregandoFigurinhas(true);
    try {
      setFigurinhas(await listarFigurinhas());
    } catch (e) {
      setAviso((e as Error).message);
      setFigurinhas([]);
    } finally {
      setCarregandoFigurinhas(false);
    }
  };

  // ── Voz ──
  async function iniciarGravacao() {
    setAviso(null);
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setAviso('Este navegador não grava áudio.');
      return;
    }
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setAviso('Libere o microfone para este site no navegador para gravar.');
      return;
    }
    // Ogg/opus é o formato da voz do WhatsApp; o Chrome grava webm/opus e a
    // Evolution converte. Qualquer um serve.
    const tipo = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus', 'audio/webm', 'audio/mp4']
      .find((t) => MediaRecorder.isTypeSupported(t)) ?? '';
    const rec = new MediaRecorder(stream, tipo ? { mimeType: tipo } : undefined);
    const partes: Blob[] = [];
    cancelarGravacao.current = false;
    rec.ondataavailable = (e) => { if (e.data.size) partes.push(e.data); };
    rec.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      setGravandoDesde(null);
      if (cancelarGravacao.current || !partes.length) return;
      const mime = rec.mimeType || tipo || 'audio/webm';
      const blob = new Blob(partes, { type: mime });
      onEnviarArquivos?.([{ arquivo: blob, nome: `audio-${Date.now()}.${extensaoDoAudio(mime)}`, voz: true }], '');
    };
    gravador.current = rec;
    rec.start(250);
    setGravandoDesde(Date.now());
    setAgora(Date.now());
  }

  function pararGravacao(enviar: boolean) {
    cancelarGravacao.current = !enviar;
    if (gravador.current && gravador.current.state !== 'inactive') gravador.current.stop();
    gravador.current = null;
  }

  // ── Enviar ──
  const podeEnviar =
    (texto.trim().length > 0 || pendentes.length > 0) && texto.length <= LIMITE && !enviando && !janela.bloqueado;

  const enviar = () => {
    if (!podeEnviar) return;
    if (pendentes.length && onEnviarArquivos) {
      onEnviarArquivos(pendentes.map(({ arquivo, nome }) => ({ arquivo, nome })), texto.trim());
      // A prévia continua viva no balão até a próxima leitura; não revogar aqui.
      setPendentes([]);
    } else {
      onEnviar(texto.trim());
    }
    setTexto('');
    setPainel(null);
  };

  const aoTeclar = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Escape' && listaAberta) {
      setListaAberta(false);
      return;
    }
    if (e.key === 'Escape' && respondendo) {
      onCancelarResposta?.();
      return;
    }
    // Com a lista aberta e uma sugestão só, Enter escolhe em vez de enviar.
    // Sem isto, quem digitasse "/saudacao" e desse Enter mandaria a mensagem
    // "/saudacao" para o cliente — literalmente.
    if (e.key === 'Enter' && !e.shiftKey && listaAberta && sugestoes.length > 0) {
      e.preventDefault();
      usarResposta(sugestoes[0]);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      enviar();
    }
  };

  const inserirEmoji = (emoji: string) => {
    const el = ref.current;
    const ini = el?.selectionStart ?? texto.length;
    const fim = el?.selectionEnd ?? texto.length;
    const novo = texto.slice(0, ini) + emoji + texto.slice(fim);
    setTexto(novo);
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(ini + emoji.length, ini + emoji.length);
    });
  };

  const restante = LIMITE - texto.length;
  const segundos = gravandoDesde ? Math.floor((agora - gravandoDesde) / 1000) : 0;
  const botaoIcone = 'mb-0.5 flex h-10 w-9 shrink-0 items-center justify-center rounded-xl transition-colors text-gray-400 hover:bg-gray-100 hover:text-primary dark:hover:bg-slate-700';

  return (
    <div
      className={`relative shrink-0 border-t border-gray-200 px-3 py-2.5 dark:border-slate-700 ${arrastando ? 'bg-primary/5' : ''}`}
      onDragOver={(e) => { if (rico && e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setArrastando(true); } }}
      onDragLeave={() => setArrastando(false)}
      onDrop={(e) => {
        if (!rico) return;
        e.preventDefault();
        setArrastando(false);
        if (e.dataTransfer?.files?.length) adicionarArquivos(e.dataTransfer.files);
      }}
    >
      {arrastando && (
        <div className="pointer-events-none absolute inset-1 z-20 flex items-center justify-center rounded-xl border-2 border-dashed border-primary/50 bg-white/80 text-sm font-semibold text-primary dark:bg-slate-900/80">
          Solte aqui para anexar
        </div>
      )}

      {(erro || aviso) && (
        <div className="mb-2 flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
          <AlertCircle size={13} className="mt-0.5 shrink-0" />
          <span className="flex-1">{erro || aviso}</span>
          <button onClick={() => { onLimparErro(); setAviso(null); }} className="shrink-0 font-semibold hover:underline">
            fechar
          </button>
        </div>
      )}

      {janela.bloqueado && !erro && (
        <p className="mb-2 flex items-start gap-1.5 rounded-lg bg-amber-50 px-3 py-1.5 text-[11px] leading-snug text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
          <Clock size={12} className="mt-0.5 shrink-0" />
          <span>{janela.motivo}</span>
        </p>
      )}

      {canalCaido && !erro && (
        <p className="mb-2 rounded-lg bg-amber-50 px-3 py-1.5 text-[11px] text-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
          A conexão está fora do ar. O envio vai ser recusado até ela voltar — o backend não
          enfileira mensagem em canal caído, para não acumular resposta que ninguém entrega.
        </p>
      )}

      {/* ⚠️ Numa thread de comentários, o que se digita aqui aparece EMBAIXO
          DO POST, à vista de qualquer pessoa. */}
      {publica && (
        <p className="mb-2 flex items-start gap-1.5 rounded-lg bg-fuchsia-50 px-3 py-1.5 text-[11px] leading-snug text-fuchsia-800 dark:bg-fuchsia-900/20 dark:text-fuchsia-300">
          <Megaphone size={12} className="mt-0.5 shrink-0" />
          <span>
            <strong>Resposta pública.</strong> Isto vai aparecer como comentário embaixo do post, e
            qualquer pessoa consegue ler. Para falar em particular, responda no direct.
          </span>
        </p>
      )}

      {caps.supportsSubject && (
        <input
          placeholder="Assunto"
          className="mb-2 w-full rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-100"
        />
      )}

      {respondendo && (
        <div className="mb-2 flex items-start gap-2 rounded-lg border-l-4 border-primary/60 bg-gray-50 px-3 py-1.5 text-xs dark:bg-slate-800">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-primary dark:text-[#B4C77A]">Respondendo a {respondendo.autor}</p>
            <p className="line-clamp-2 text-gray-600 dark:text-slate-300">{respondendo.texto}</p>
          </div>
          <button onClick={onCancelarResposta} title="Cancelar resposta" className="shrink-0 rounded p-0.5 text-gray-400 hover:text-gray-700 dark:hover:text-slate-200">
            <X size={14} />
          </button>
        </div>
      )}

      {pendentes.length > 0 && (
        <div className="mb-2">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {pendentes.map((p) => {
              const mime = p.arquivo.type || '';
              return (
                <div key={p.id} className="relative flex h-20 w-20 shrink-0 flex-col items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 text-center dark:border-slate-600 dark:bg-slate-800">
                  {p.previa && mime.startsWith('image/') ? (
                    <img src={p.previa} alt={p.nome} className="h-full w-full object-cover" />
                  ) : (
                    <>
                      {mime.startsWith('video/') ? <Video size={20} className="text-gray-400" /> : <IconeArquivo size={20} className="text-gray-400" />}
                      <span className="mt-1 line-clamp-2 px-1 text-[9px] leading-tight text-gray-600 dark:text-slate-300">{p.nome}</span>
                      <span className="text-[9px] text-gray-400">{tamanhoLegivel(p.arquivo.size)}</span>
                    </>
                  )}
                  <button
                    onClick={() => tirarPendente(p.id)}
                    title="Tirar"
                    className="absolute right-0.5 top-0.5 rounded-full bg-black/60 p-0.5 text-white hover:bg-black/80"
                  >
                    <X size={11} />
                  </button>
                </div>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400 dark:text-slate-500">
            {pendentes.length > 1 ? `${pendentes.length} arquivos. O texto abaixo vai como legenda do primeiro.` : 'O texto abaixo vai como legenda.'}
          </p>
        </div>
      )}

      {sugestoes.length > 0 && (
        <div className="mb-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg dark:border-slate-600 dark:bg-slate-800">
          {sugestoes.map((r) => (
            <button
              key={r.id}
              onClick={() => usarResposta(r)}
              className="flex w-full items-start gap-2 px-3 py-1.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-slate-700"
            >
              <span className="shrink-0 rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-primary">
                /{r.atalho}
              </span>
              <span className="line-clamp-1 flex-1 text-xs text-gray-600 dark:text-slate-300">{r.body}</span>
            </button>
          ))}
        </div>
      )}

      {painel === 'emoji' && (
        <div className="mb-2 max-h-56 overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-slate-600 dark:bg-slate-800">
          {GRUPOS_EMOJI.map((g) => (
            <div key={g.nome} className="mb-1.5">
              <p className="px-1 pb-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">{g.nome}</p>
              <div className="flex flex-wrap">
                {g.emojis.map((e, i) => (
                  <button key={`${g.nome}-${i}`} onClick={() => inserirEmoji(e)} className="rounded p-1 text-xl leading-none transition-transform hover:scale-125 hover:bg-gray-100 dark:hover:bg-slate-700">
                    {e}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {painel === 'figurinhas' && (
        <div className="mb-2 rounded-xl border border-gray-200 bg-white p-2 shadow-lg dark:border-slate-600 dark:bg-slate-800">
          <div className="mb-1.5 flex items-center justify-between px-1">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Figurinhas já usadas</p>
            <button
              onClick={() => inputFigurinha.current?.click()}
              className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-[11px] font-semibold text-primary hover:bg-primary/10"
            >
              <IconeImagem size={12} /> Criar de uma imagem
            </button>
          </div>
          <div className="max-h-48 overflow-y-auto">
            {carregandoFigurinhas ? (
              <div className="flex justify-center py-6"><Loader2 size={18} className="animate-spin text-gray-300" /></div>
            ) : figurinhas && figurinhas.length ? (
              <div className="grid grid-cols-6 gap-1 sm:grid-cols-8">
                {figurinhas.map((f) => (
                  <button
                    key={f.caminho}
                    onClick={() => { onEnviarFigurinha?.(f); setPainel(null); }}
                    className="rounded-lg p-1 transition-transform hover:scale-110 hover:bg-gray-100 dark:hover:bg-slate-700"
                  >
                    <img src={f.url} alt="Figurinha" className="h-14 w-14 object-contain" loading="lazy" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="px-1 py-4 text-center text-xs text-gray-400">Nenhuma figurinha ainda. Crie uma a partir de uma imagem.</p>
            )}
          </div>
        </div>
      )}

      <input
        ref={inputArquivo}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => { if (e.target.files) adicionarArquivos(e.target.files); e.target.value = ''; }}
      />
      <input
        ref={inputFigurinha}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          if (f.size > 5 * MB) { setAviso('A imagem da figurinha passa de 5 MB.'); return; }
          onEnviarArquivos?.([{ arquivo: f, nome: f.name || 'figurinha.png', figurinha: true }], '');
          setPainel(null);
        }}
      />

      {gravandoDesde ? (
        <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 dark:border-red-900/60 dark:bg-red-900/20">
          <button onClick={() => pararGravacao(false)} title="Descartar" className="rounded-full p-1.5 text-gray-500 hover:bg-white hover:text-red-600 dark:hover:bg-slate-800">
            <Trash2 size={16} />
          </button>
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-red-500" />
          <span className="flex-1 text-sm tabular-nums text-red-700 dark:text-red-300">
            Gravando {String(Math.floor(segundos / 60)).padStart(1, '0')}:{String(segundos % 60).padStart(2, '0')}
          </span>
          <button
            onClick={() => pararGravacao(true)}
            aria-label="Enviar áudio"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-white shadow-sm hover:bg-primary/90"
          >
            <Send size={16} />
          </button>
        </div>
      ) : (
        <div className="flex items-end gap-1">
          <button onClick={() => setPainel((p) => (p === 'emoji' ? null : 'emoji'))} title="Emoji" className={`${botaoIcone} ${painel === 'emoji' ? 'bg-primary/10 text-primary' : ''}`}>
            <Smile size={18} />
          </button>
          {rico && (
            <>
              <button onClick={() => inputArquivo.current?.click()} title="Anexar foto, vídeo ou documento (ou cole/arraste aqui)" className={botaoIcone}>
                <Paperclip size={18} />
              </button>
              {(onEnviarFigurinha || listarFigurinhas) && (
                <button onClick={abrirFigurinhas} title="Figurinhas" className={`${botaoIcone} ${painel === 'figurinhas' ? 'bg-primary/10 text-primary' : ''}`}>
                  <Sticker size={18} />
                </button>
              )}
            </>
          )}
          {disponiveis.length > 0 && (
            <button
              onClick={() => {
                setListaAberta((v) => !v);
                if (!listaAberta && !texto) setTexto('/');
                ref.current?.focus();
              }}
              title="Respostas rápidas (digite / no começo)"
              className={`${botaoIcone} ${listaAberta ? 'bg-primary/10 text-primary' : ''}`}
            >
              <Zap size={16} />
            </button>
          )}
          <textarea
            ref={ref}
            rows={1}
            value={texto}
            onChange={(e) => {
              setTexto(e.target.value);
              setListaAberta(e.target.value.startsWith('/'));
            }}
            onKeyDown={aoTeclar}
            onPaste={aoColar}
            placeholder={publica
              ? 'Escreva a resposta pública…  (Enter envia, Shift+Enter quebra linha)'
              : pendentes.length
                ? 'Legenda (opcional)…'
                : 'Escreva uma mensagem…  (Enter envia, Shift+Enter quebra linha)'}
            className="max-h-[140px] flex-1 resize-none rounded-2xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-sm leading-relaxed text-gray-800 placeholder:text-gray-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary dark:border-slate-600 dark:bg-slate-900/60 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:bg-slate-900"
          />
          {podeGravar && !texto.trim() && !pendentes.length ? (
            <button
              onClick={iniciarGravacao}
              disabled={enviando || janela.bloqueado}
              aria-label="Gravar mensagem de voz"
              title="Gravar mensagem de voz"
              className="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-sm transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-slate-700"
            >
              <Mic size={17} />
            </button>
          ) : (
            <button
              onClick={enviar}
              disabled={!podeEnviar}
              aria-label="Enviar mensagem"
              className="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-white shadow-sm transition-all hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-slate-700 dark:disabled:text-slate-500"
            >
              {enviando ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
            </button>
          )}
        </div>
      )}

      {restante < 200 && (
        <p className={`mt-1 text-right text-[11px] ${restante < 0 ? 'font-semibold text-red-600' : 'text-gray-400 dark:text-slate-500'}`}>
          {restante < 0 ? `${-restante} caracteres além do limite` : `${restante} caracteres restantes`}
        </p>
      )}
    </div>
  );
}
